import { Card, Text, Checkbox, Divider, Group, Radio } from "@mantine/core";
import { IconParkingCircle } from "@tabler/icons-react";
import { ImageWithBoundingBox } from "@labeling-tools/ui-components/src/ImageWithBoundingBox";
import { ViewMode } from "@/types";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import { ActionStatusBadge } from "./ActionStatusBadge";
import { QaQuestionAction } from "./QaQuestionAction";
import { useEmptyAreaQuestion } from "@/hooks/useEmptyAreaQuestion";

// NOTE: the option labels in this card (radio pair + qualifier checkboxes)
// must stay in sync with the EMPTY_AREA_OPTIONS labels in @/config/data, which
// drive the QA "which parts were incorrect?" multiselect.
export const EmptyAreaCard = ({
  missingItemImageUrl,
  emptyAreaPoints,
  mode,
  task,
  onOpenPlanogram,
}: {
  missingItemImageUrl: string;
  emptyAreaPoints: any[];
  mode?: ViewMode;
  task: JobTaskDetailSchema;
  onOpenPlanogram?: () => void;
}) => {
  const {
    emptyAreaRadioValue,
    emptyAreaConfirmed,
    resultMetadata,
    handleEmptyAreaRadioChange,
    handleQualifierChange,
  } = useEmptyAreaQuestion();

  return (
    <Card shadow="lg" padding="lg" radius="md" withBorder className="flex-1">
      <div className="flex flex-col">
        <div
          className="flex items-center justify-between"
          style={{ marginBottom: "10px" }}
        >
          <Text size="md" fw={500} className="text-gray-700 text-left">
            Empty Area Detected
          </Text>
          {onOpenPlanogram && (
            <IconParkingCircle
              className="text-gray-500 cursor-pointer flex-shrink-0"
              onClick={onOpenPlanogram}
              title="Open planogram"
            />
          )}
        </div>
        <ImageWithBoundingBox
          src={missingItemImageUrl}
          alt="Missing Item"
          points={emptyAreaPoints}
          variant="empty"
          clickable={mode !== "admin"}
          enablePulsing={true}
          crop={true}
        />
      </div>
      <Divider style={{ margin: "20px 0" }} />
      <div className="flex flex-col" style={{ marginTop: "auto" }}>
        <Text
          size="md"
          fw={500}
          className="text-left"
          style={{ marginBottom: "10px" }}
        >
          Select all that apply
        </Text>
        <Group mb="sm">
          {/* non "viewer" mode checkboxes are disabled and use the result from the task */}
          {mode !== "viewer" && (
            <Checkbox
              label="Is Empty Area"
              checked={
                (typeof task.result?.empty_area_selected === "boolean"
                  ? task.result?.empty_area_selected
                  : task.result?.empty_area_confirmed) ||
                (typeof task.data?.confirmation_result?.empty_area_selected ===
                "boolean"
                  ? task.data?.confirmation_result?.empty_area_selected
                  : task.data?.confirmation_result?.empty_area_confirmed)
              }
              disabled={true}
            />
          )}
          {mode !== "viewer" && (
            <Checkbox
              label="Not Empty Area"
              checked={
                task.result?.metadata?.not_empty_area === true ||
                task.data?.confirmation_result?.metadata?.not_empty_area ===
                  true
              }
              disabled={true}
            />
          )}
          {mode !== "viewer" && (
            <Checkbox
              label="Not Main Aisle"
              checked={
                task.result?.metadata?.not_main_aisle === true ||
                task.data?.confirmation_result?.metadata?.not_main_aisle ===
                  true
              }
              disabled={true}
            />
          )}
          {mode !== "viewer" && (
            <Checkbox
              label="Unblurred Person in Image"
              checked={
                task.result?.metadata?.unblurred_person_in_image === true ||
                task.data?.confirmation_result?.metadata
                  ?.unblurred_person_in_image === true
              }
              disabled={true}
            />
          )}
          {mode !== "viewer" && (
            <Checkbox
              label="Remaining Product Facings"
              checked={
                task.result?.metadata?.remaining_product_facings === true ||
                task.data?.confirmation_result?.metadata
                  ?.remaining_product_facings === true
              }
              disabled={true}
            />
          )}
          {mode !== "viewer" && (
            <Checkbox
              label="Blue Tag Present"
              checked={
                task.result?.metadata?.blue_tag_present === true ||
                task.data?.confirmation_result?.metadata?.blue_tag_present ===
                  true
              }
              disabled={true}
            />
          )}
        </Group>

        {/* "viewer" mode: the empty-area question is a radio choice,
            with the four qualifier checkboxes appearing only once the
            labeler has engaged with the qualifier flow. */}
        {mode === "viewer" && (
          <Radio.Group
            value={emptyAreaRadioValue}
            onChange={handleEmptyAreaRadioChange}
            mb="sm"
          >
            <Group>
              <Radio value="empty" label="Is Empty Area" />
              <Radio value="not_empty" label="Not Empty Area" />
            </Group>
          </Radio.Group>
        )}
        {mode === "viewer" && emptyAreaConfirmed && (
          <Group mb="sm">
            <Checkbox
              label="Not Main Aisle"
              checked={!!resultMetadata.not_main_aisle}
              onChange={handleQualifierChange("not_main_aisle")}
            />
            <Checkbox
              label="Unblurred Person in Image"
              checked={!!resultMetadata.unblurred_person_in_image}
              onChange={handleQualifierChange("unblurred_person_in_image")}
            />
            <Checkbox
              label="Remaining Product Facings"
              checked={!!resultMetadata.remaining_product_facings}
              onChange={handleQualifierChange("remaining_product_facings")}
            />
            <Checkbox
              label="Blue Tag Present"
              checked={!!resultMetadata.blue_tag_present}
              onChange={handleQualifierChange("blue_tag_present")}
            />
          </Group>
        )}

        {/* "admin" mode shows action status */}
        {mode === "admin" && (
          <ActionStatusBadge task={task} resultField="empty_area_confirmed" />
        )}

        {/* "confirmation-qa" mode shows action status from data */}
        {(mode === "confirmation-qa" ||
          mode === "confirmation-qa-feedback") && (
          <>
            <ActionStatusBadge
              task={{
                ...task,
                result: task.data?.confirmation_result || ({} as any),
              }}
              resultField="empty_area_confirmed"
            />
            <QaQuestionAction questionNumber={1} task={task} />
          </>
        )}
      </div>
    </Card>
  );
};
